import { dash, decl, hash, name, trim } from "./utils";
import { useInjectRules } from "#internal";

export type FontFace = {
  /**
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/%40font-face/src)
   */
  src: string;
  /**
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/%40font-face/ascent-override)
   */
  ascentOverride?: string;
  /**
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/%40font-face/descent-override)
   */
  descentOverride?: string;
  /**
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/%40font-face/font-display)
   */
  fontDisplay?: string;
  /**
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/%40font-face/font-stretch)
   */
  fontStretch?: string;
  /**
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/%40font-face/font-style)
   */
  fontStyle?: string;
  /**
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/%40font-face/font-weight)
   */
  fontWeight?: string;
  /**
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/%40font-face/font-feature-settings)
   */
  fontFeatureSettings?: string;
  /**
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/%40font-face/font-variation-settings)
   */
  fontVariationSettings?: string;
  /**
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/%40font-face/line-gap-override)
   */
  lineGapOverride?: string;
  /**
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/%40font-face/size-adjust)
   */
  sizeAdjust?: string;
  /**
   * [MDN Reference](https://developer.mozilla.org/en-US/docs/Web/CSS/%40font-face/unicode-range)
   */
  unicodeRange?: string;
};

const knownProperties = new Set(
  Object.keys({
    src: null,
    ascentOverride: null,
    descentOverride: null,
    fontDisplay: null,
    fontStretch: null,
    fontStyle: null,
    fontWeight: null,
    fontFeatureSettings: null,
    fontVariationSettings: null,
    lineGapOverride: null,
    sizeAdjust: null,
    unicodeRange: null,
  } satisfies {
    [K in keyof FontFace]-?: null;
  }),
);

/**
 * Apply `@font-face` atomically
 *
 * @example
 * ```tsx
 * const fontFace = useFontFace();
 *
 * <div
 *   css={{
 *     fontFamily: fontFace({
 *       src: `url(${fontUrl})`,
 *     }),
 *   }}
 * />
 * ```
 */
export const useFontFace = () => {
  const { dangerouslyAllowUnknownProperties, injectRules } = useInjectRules();

  return (...value: FontFace[]) => {
    const [className, rules] = composeRules(value, {
      dangerouslyAllowUnknownProperties,
    });
    injectRules(className, rules);
    return className;
  };
};

const composeRules = (
  fontFaces: FontFace[],
  {
    dangerouslyAllowUnknownProperties,
  }: {
    dangerouslyAllowUnknownProperties: boolean | undefined;
  },
) => {
  let h = hash();
  const rules = [];
  for (const ff of fontFaces) {
    const entries = Object.entries(ff)
      .filter((e): e is [string, string] => Boolean(e[1]))
      .sort(([l], [r]) => (l < r ? -1 : l > r ? 1 : 0));
    let rule = "";
    for (const [property, value] of entries) {
      if (knownProperties.has(property) || dangerouslyAllowUnknownProperties) {
        const p = dash(property);
        const v = trim(value);
        h = hash(p, h);
        h = hash(v, h);
        rule += `${decl(p, v)};`;
      }
    }
    rules.push(rule);
  }
  const n = name(h);
  return [
    n,
    rules.map((rule) => `@font-face{font-family:${n};${rule}}`),
  ] as const;
};
